import React, { lazy, Suspense, useEffect, useState, useCallback } from 'react';
import './App.css';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

import axios from 'axios';
import Cookies from 'js-cookie';
import Landing from 'pages/landing/Landing';
import Header from 'shared/layout/Header';
import Login from 'pages/auth/Login';
import Profile from 'pages/profile/Profile';
import NotificationBell from 'shared/layout/NotificationBell';
import UserTypeModal from 'shared/modals/UserTypeModal';
import { Helmet } from 'react-helmet';
import GlobalContextMenu from 'shared/layout/GlobalContextMenu';
import CookieConsent from 'shared/components/CookieConsent';
import { useCookieConsent } from 'contexts/CookieConsentContext';
import { getUserTypeFromJwt, isAdminFromJwt } from 'utils/jwt';
import { setAnalyticsConsent } from 'services/analyticsService';
import { offersConfig } from 'app/config/offersConfig';

const Helpdesk = lazy(() => import('pages/helpdesk/Helpdesk'));
const Invoices = lazy(() => import('pages/invoices/Invoices'));
const Settings = lazy(() => import('pages/settings/Settings'));
const SubscriptionRouterPage = lazy(() => import('app/routes/SubscriptionRouterPage'));
const GlobalStats = lazy(() => import('pages/stats/GlobalStats'));
const Sponsor = lazy(() => import('pages/sponsor/Sponsor'));
const DevTools = lazy(() => import('pages/devtools/DevTools'));
const CGU = lazy(() => import('pages/legal/CGU'));
const CGV = lazy(() => import('pages/legal/CGV'));

function App() {
  const [isLoginVisible, setIsLoginVisible] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(!!Cookies.get('user_token'));
  const [registerMode, setRegisterMode] = useState(false);
  const [isUserTypeModalOpen, setIsUserTypeModalOpen] = useState(false);
  const { consent } = useCookieConsent();
  const canOpenDevTools = isAuthenticated && isAdminFromJwt(Cookies.get('user_token'));

  useEffect(() => {
    setAnalyticsConsent(consent === 'accepted');
  }, [consent]);

  const ensureUserType = useCallback(() => {
    const jwt = Cookies.get('user_token');

    if (!jwt) {
      setIsUserTypeModalOpen(false);
      return;
    }

    const userType = getUserTypeFromJwt(jwt);
    setIsUserTypeModalOpen(!userType);
  }, []);

  useEffect(() => {
    const jwt = Cookies.get('user_token');
    if (jwt) {
      setIsAuthenticated(true);
    } else {
      setIsAuthenticated(false);
    }

    if (!jwt) {
      try {
        const params = new URLSearchParams(window.location.search);
        const sponsorParam = params.get('sponsor');
        if (sponsorParam) {
          // Decode base64 (URL-safe, with padding handling)
          const decodeBase64Url = (input) => {
            try {
              let b64 = input.replace(/-/g, '+').replace(/_/g, '/');
              while (b64.length % 4 !== 0) b64 += '=';
              const binary = atob(b64);
              try {
                return decodeURIComponent(Array.prototype.map.call(binary, (c) =>
                  '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
                ).join(''));
              } catch (_) {
                return binary;
              }
            } catch (_) {
              return null;
            }
          };

          const decodedSponsor = decodeBase64Url(sponsorParam) || sponsorParam;

          // Persist decoded sponsor for later registration
          Cookies.set('sponsor', decodedSponsor, { expires: 30 });

          // Optionally clean the URL to avoid leaking the code
          params.delete('sponsor');
          const search = params.toString();
          const newUrl = window.location.pathname + (search ? `?${search}` : '') + window.location.hash;
          window.history.replaceState({}, '', newUrl);
        }
      } catch (e) {
        // No-op if URL parsing fails
      }
    }
    ensureUserType();
  }, [ensureUserType]);

  const handleLoginButtonClick = () => {
    setRegisterMode(false);
    setIsLoginVisible(true);
  };

  const handleRegisterButtonClick = () => {
    setRegisterMode(true);
    setIsLoginVisible(true);
  };

  const handleHomeButtonClick = () => {
    const jwt = Cookies.get('user_token');

    if (!jwt) {
      setIsAuthenticated(false);
      setIsLoginVisible(false);
    }
  };

  const handleLogin = () => {
    setIsAuthenticated(true);
    ensureUserType();
  };

  const handleLogout = useCallback(() => {
    Cookies.remove('user_token');
    setIsAuthenticated(false);
    setIsLoginVisible(false);
    setIsUserTypeModalOpen(false);
  }, []);

  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error?.response?.status === 401 && Cookies.get('user_token')) {
          handleLogout();
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, [handleLogout]);

  const offers = offersConfig.map(offer => {
    const newFeaturesElements = [...offer.featuresElements];
    if (offer.commissionRate > 0) {
      newFeaturesElements.push(`✓ Commission de <b>${offer.commissionRate}%</b>*`);
    }
    newFeaturesElements.push("✓ Sans engagement");
    return {
      ...offer,
      featuresElements: newFeaturesElements,
    };
  });

  const HomePage = () => (
    <>
      <Helmet>
        <title>Unbot</title>
        <meta name="description" content="Unbot : Assistant IA pour Uncove" />
        <meta name="keywords" content="Unbot, assistant IA Uncove, automatisation chats, gestion médias IA" />
      </Helmet>
      {
        isAuthenticated &&
        <>
          <Profile
            offers={offers}
            onLogoutButtonClicked={handleLogout}
          />
        </>
      }

      {
        !isAuthenticated &&
        <>
          {isLoginVisible ? (
            <Login
              register={registerMode}
              onLogin={handleLogin}
              onClose={() => setIsLoginVisible(false)}
            />
          ) : (
            <>
              <Landing
                offers={offers}
                onRegisterButtonClicked={handleRegisterButtonClick}
              />
            </>
          )}
        </>
      }
    </>
  );

  return (
    <Router>
      <div className="App">
        <Header
          key={isAuthenticated}
          onLoginButtonClicked={handleLoginButtonClick}
          onHomeButtonClicked={handleHomeButtonClick}
          isLogged={isAuthenticated}
          actions={isAuthenticated ? <NotificationBell /> : null}
        />
        <GlobalContextMenu onReturnToMenu={handleHomeButtonClick} />

        <Suspense fallback={null}>
          <Routes>
            <Route path="/helpdesk" element={<Helpdesk isLogged={isAuthenticated} />} />
            <Route path="/invoices" element={isAuthenticated ? <Invoices /> : <Navigate to="/" />} />
            <Route path="/settings" element={isAuthenticated ? <Settings /> : <Navigate to="/" />} />
            <Route path="/stats" element={isAuthenticated ? <GlobalStats /> : <Navigate to="/" />} />
            <Route path="/subscription/*" element={isAuthenticated ? <SubscriptionRouterPage /> : <Navigate to="/" />} />
            <Route path="/sponsor" element={isAuthenticated ? <Sponsor /> : <Navigate to="/" />} />
            <Route path="/dev-tools" element={canOpenDevTools ? <DevTools /> : <Navigate to="/" />} />
            <Route path="/cgu" element={<CGU />} />
            <Route path="/cgv" element={<CGV />} />
            <Route path="/*" element={<HomePage />} />
          </Routes>
        </Suspense>
        <UserTypeModal open={isUserTypeModalOpen} />
        <CookieConsent />
      </div>
    </Router>
  );
}

export default App;
